import { checkoutWithCard, encryptAndCheckoutWithNewCard, splitNames } from '@/helpers/clickToPayHelper';
import vars from '@/config/vars';
import axios from '@/config/axios.config';
import PaymentTypes from '@/models/PaymentTypes';
import { logException } from '@/helpers/cieloLogger';

export default {
    namespaced: true,

    state: {
        dpaId: '',
        dpaName: '',
        isClickToPayEnabled: false,
        initialized: false,
        availableCardBrands: [],
        cards: null,
        consumer: {
            email: '',
            phone: '',
            nationalIdentification: '',
            fullName: '',
            countryCode: '55'
        },
        consumerPresent: false,
        networkId: '',
        maskedValidationChannel: '',
        supportedValidationChannels: [],
        rememberMeAgreement: false,
        otpValidation: {
            code: '',
            disableElements: false,
            isSuccessful: false,
            error: '',
            errorReason: '',
        },
        checkoutResponse: {
            actionCode: '',
            correlationId: '',
            merchantTransactionId: '',
            xSrcCxFlowId: '',
            cardPanLastFour: '',
            cardArtUri: '',
            maskedFullName: '',
        },
        cardBrandError: '',
        recognitionToken: '',
        supportedBrandCards: [],
    },

    getters: {
        getSubmitModel(state, getters, rootState, rootGetters) {
            const submitModel = {
                ...rootGetters['cardPayment/getSubmitModel'],
                PaymentType: PaymentTypes.CREDIT_CARD,
                Guest: {
                    FullName: state.consumer.fullName,
                    Email: state.consumer.email,
                    Phone: state.consumer.phone,
                    Identity: state.consumer.nationalIdentification,
                    AcceptAds: rootState.customer.AcceptAds,
                    IsForeign:
						        rootGetters['billingAddress/getIsNational'] == false,
                    UserAgent: rootState.customer.UserAgent
                },
                clickToPay: {
                    correlationId: state.checkoutResponse.correlationId,
                    merchantTransactionId: state.checkoutResponse.merchantTransactionId,
                    xSrcCxFlowId: state.checkoutResponse.xSrcCxFlowId,
                    dpaId: state.dpaId,
                }
            };

            const billingModel = rootGetters['billingAddress/getSubmitModel'];
            if(!billingModel.BillingAddress) {
                return submitModel;
            }

            const isBillingValidForClickToPay = billingModel.BillingAddress.IsBillingAddressSameAsShipping
                || (billingModel.BillingAddress.ZipCode && billingModel.BillingAddress.City);

            if(!isBillingValidForClickToPay) {
                submitModel.Card.BillingAddress = null;
            }

            return submitModel;
        },
        getCard(state, getters, rootState) {
            const { firstName, lastName } = splitNames(rootState.cardPayment.Holder);
            const billingAddress = rootState.billingAddress.SameAsShippingAddress ? {
                name: rootState.cardPayment.Holder,
                line1: rootState.shippingAddress.Street,
                line2: rootState.shippingAddress.Number,
                city: rootState.shippingAddress.City,
                state: rootState.shippingAddress.State,
                zip: rootState.shippingAddress.ZipCode,
                countryCode: 'BR',
            } : {
                name: rootState.cardPayment.Holder,
                line1: rootState.billingAddress.Street,
                line2: rootState.billingAddress.Number,
                city: rootState.billingAddress.City,
                state: rootState.billingAddress.State,
                zip: rootState.billingAddress.ZipCode,
                countryCode: rootState.billingAddress.Type === 'national' ? 'BR': '',
            };
            return {
                panExpirationMonth: rootState.cardPayment.Expiration.Month.toString(),
                panExpirationYear: rootState.cardPayment.Expiration.Year.toString(),
                primaryAccountNumber: rootState.cardPayment.CardNumber.toString(),
                cardSecurityCode: rootState.cardPayment.SecurityCode.toString(),
                cardholderFirstName: firstName,
                cardholderLastName: lastName,
                billingAddress,
            };
        },

        getConsumerFirstDigitsC2p(state) {
            return state.consumer.nationalIdentification ? state.consumer.nationalIdentification.slice(0, 3): '***';
        },

        getConsumerName(state) {
            return state.consumer.fullName ? state.consumer.fullName: '';
        },

        getRecognitionToken: (state) => state.recognitionToken,
    },

    mutations: {
        setFromRequest(state, data) {
            if(data.WalletMethodInformation && data.SupportedCardBrands) {
                const c2pCredentials = data.WalletMethodInformation.find(item => item.Name === 'ClickToPay') || null;
                const supportedCardBrands = data.SupportedCardBrands.filter(obj => obj.Wallets && obj.Wallets.includes('ClickToPay')).map(obj => obj.Name) || [];

                if(c2pCredentials && supportedCardBrands.length) {
                    state.dpaId = c2pCredentials.WalletCredentials.DpaId;
                    state.isClickToPayEnabled = true;
                    state.dpaName = data.MerchantLayoutConfiguration? 
                        data.MerchantLayoutConfiguration.Nickname: '';
                    state.supportedBrandCards = supportedCardBrands.map(brand => brand.toLowerCase());
                }
            }
        },
        setCards(state, cards) {
            state.cards = cards;
        },
        setConsumer(state, { email, phone, nationalIdentification, fullName }) {
            state.consumer.email = email;
            state.consumer.phone = phone;
            state.consumer.nationalIdentification = nationalIdentification;
            state.consumer.fullName = fullName;
        },
        setRememberMe(state, rememberMe) {
            state.rememberMeAgreement = rememberMe;
        },
        setValidationChannels(state, { validationChannel, supportedChannels, networkId }) {
            state.networkId = networkId;
            state.maskedValidationChannel = validationChannel;
            state.supportedValidationChannels = supportedChannels;
        },
        setOtpValidationCode(state, code) {
            state.otpValidation.code = code;
        },
        setOtpValidationDisableElements(state, disableElements) {
            state.otpValidation.disableElements = disableElements;
        },
        setOtpValidationIsSuccessful(state, success) {
            state.otpValidation.isSuccessful = success;
        },
        setOtpValidationError(state, error) {
            if(!error.reason) {
                state.otpValidation.error = 'otpUnknownError';
                state.otpValidation.errorReason = 'INVALID_ERROR_REASON';
                return;
            }

            state.otpValidation.errorReason = error.reason;

            if(error.reason === 'CODE_INVALID') {
                state.otpValidation.error = 'otpInvalidCodeError';
                return;
            }

            if(error.reason === 'ACCT_INACCESSIBLE') {
                state.otpValidation.error = 'otpInaccessibleError';
                return;
            }

            if(error.reason === 'RETRIES_EXCEEDED') {
                state.otpValidation.error = 'otpRetriesExceededError';
                return;
            }

            state.otpValidation.error = 'otpUnknownError';
        },
        clearOtpValidationError(state) {
            state.otpValidation.error = '';
            state.otpValidation.errorReason = '';
        },
        clearOtpStates(state) {
            state.otpValidation.code = '';
            state.otpValidation.disableElements = false;
            state.otpValidation.isSuccessful = false;
            state.otpValidation.error = '';
            state.otpValidation.errorReason = '';
        },
        setCheckoutResponse(state, result) {
            state.checkoutResponse.actionCode = result.checkoutActionCode;

            if(result.checkoutActionCode !== 'COMPLETE') {
                return;
            }

            state.checkoutResponse.correlationId = result.checkoutResponseData.srcCorrelationId;
            state.checkoutResponse.merchantTransactionId = result.headers['merchant-transaction-id'];
            state.checkoutResponse.xSrcCxFlowId = result.headers['x-src-cx-flow-id'];

            const maskedCard = result.checkoutResponseData.maskedCard;
            if(maskedCard) {
                state.checkoutResponse.cardPanLastFour = maskedCard.panLastFour;
                state.checkoutResponse.cardArtUri = 
                    maskedCard.digitalCardData ? maskedCard.digitalCardData.artUri : '';
            }

            if(result.checkoutResponseData.maskedConsumer) {
                state.checkoutResponse.maskedFullName = result.checkoutResponseData.maskedConsumer.maskedFullName;
            }
        },
        setCardBrandError(state, errorCode) {
            state.cardBrandError = errorCode;
        },
        clearErrors(state) {
            state.cardBrandError = '';
        },
        setRecognitionToken(state, recognitionToken) {
            state.recognitionToken = recognitionToken;
        }
    },

    actions: {
        async checkoutWithNewCard({ dispatch, getters, state }) {
            const { firstName, lastName } = splitNames(state.consumer.fullName);
            const consumerPayload = {
                emailAddress: state.consumer.email,
                mobileNumber: {
                    phoneNumber: state.consumer.phone,
                    countryCode: state.consumer.countryCode
                },
                nationalIdentifier: state.consumer.nationalIdentification,
                firstName,
                lastName,
            };

            const card = getters['getCard'];
            const result = await encryptAndCheckoutWithNewCard({
                ...card
            }, consumerPayload, state.rememberMeAgreement);

            dispatch('handleCheckoutResult', result);
        },

        async checkoutWithCard({ dispatch, state }, srcDigitalCardId) {
            const result = await checkoutWithCard(srcDigitalCardId, state.rememberMeAgreement);
            dispatch('handleCheckoutResult', result);
        },

        async idLookup({ dispatch, state }, email) {
            const getIdLookupResult = await window.mcCheckoutService.idLookup({
                email,
            });

            state.consumerPresent = getIdLookupResult.consumerPresent;
            if(getIdLookupResult.consumerPresent === false) {
                return;
            }
        },

        async validateOtp({ commit, state }) {
            try {
                commit('setOtpValidationDisableElements', true);
                commit('clearOtpValidationError');
                
                const validationResult = await window.mcCheckoutService.validate({
                    value: state.otpValidation.code
                });
                
                commit('setCards', validationResult);
                commit('setOtpValidationIsSuccessful', true);
            } catch (error) {
                commit('setOtpValidationIsSuccessful', false);
                commit('setOtpValidationDisableElements', false);
                commit('setOtpValidationError', error);
            }
        },

        handleCheckoutResult({ commit, dispatch }, result) {
            if(!result) {
                commit('setCheckoutResponse', {
                    checkoutActionCode: 'NULL_RESPONSE'
                });
                return;
            }

            if(result.recognitionToken) {
                try {
                    axios.post('/recognitionToken/saveToken', {token: result.recognitionToken});
                } catch (error) {
                    logException(error);
                }
            }

            commit('setCheckoutResponse', result);
        },

        async initiateValidation({ commit }, validationChannel = null) {
            const getInitiateValidationResult = validationChannel? 
                await window.mcCheckoutService.initiateValidation({
                    requestedValidationChannelId: validationChannel
                }): 
                await window.mcCheckoutService.initiateValidation();

            commit('setValidationChannels', { 
                networkId: getInitiateValidationResult.network,
                validationChannel: getInitiateValidationResult.maskedValidationChannel, 
                supportedChannels: getInitiateValidationResult.supportedValidationChannels 
            });
        },

        setPaymentMethod({ rootGetters, rootState }, paymentMethodId) {
            let paymentMethod = rootGetters['payment/getPaymentMethodById'](paymentMethodId);
            if (!paymentMethod)
                throw new Error('Meio de pagamento não suportado');

            rootState.payment.PaymentMethodId = paymentMethod.Id;
        },

        async fetchCardBrand({ dispatch, commit, rootState, state }, cardBin) {
            commit('clearErrors');
            let paymentType = PaymentTypes.CREDIT_CARD;
            let preorderId = rootState.preorder.Id;

            let response;
            
            if (vars.USE_SIMULATED_MODE)
                response = await new Promise(resolve => setTimeout(() => resolve(buildSimulatedResponse()), 1000));
            else
                response = await axios.get(`brand/${preorderId}/${paymentType}/${cardBin}`);
            
            if(!response || !response.data) {
                commit('setCardBrandError', 'cardBrandNotSupported');
                throw Error('cardBrandNotSupported');
            }

            const isVisaAvailable = state.availableCardBrands.find(b => b === 'visa');
            if(!isVisaAvailable && response.data.Brand === 'Visa') {
                commit('setCardBrandError', 'cardBrandNotSupported');
                throw Error('cardBrandNotSupported');
            }

            const isMasterAvailable = state.availableCardBrands.find(b => b === 'mastercard');
            if(!isMasterAvailable && response.data.Brand === 'Master') {
                commit('setCardBrandError', 'cardBrandNotSupported');
                throw Error('cardBrandNotSupported');
            }

            if(response.data.Brand !== 'Master' && response.data.Brand !== 'Visa') {
                commit('setCardBrandError', 'cardBrandNotSupported');
                throw Error('cardBrandNotSupported');
            }

            let paymentMethodId = response.data.PaymentMethodId;
            let maxNumberOfPayments = 0;

            if(response.data.InstallmentsInfo !== null && response.data.InstallmentsInfo.length > 0)
                maxNumberOfPayments = response.data.InstallmentsInfo.length;

            commit(
                'payment/setPaymentMethodNumberOfInstallments', 
                {
                    paymentMethodId, 
                    maxNumberOfPayments
                },
                { root: true });
            dispatch('setPaymentMethod', paymentMethodId);
        }
    }
};

function buildSimulatedResponse() {
    return {
        data: {
            Brand:'Visa',
            PaymentMethodId: 10, 
            InstallmentsInfo: [
                { Installments: 1 },
                { Installments: 2 },
                { Installments: 3 },
                { Installments: 4 },
                { Installments: 5 },
                { Installments: 6 },
                { Installments: 7 },
                { Installments: 8 }
            ]
        }
    };
}